// @core/stores/form/FormStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { formApi } from '@core/api';
import type {
  ArchiveFormRequest,
  CloseFormRequest,
  CreateFormFieldRequest,
  CreateFormRequest,
  CreateFormSubmissionRequest,
  CreateFormTemplateRequest,
  DuplicateFormRequest,
  ExportFormSubmissionsResponse,
  Form,
  FormAnalyticsResponse,
  FormFieldResponse,
  FormResponse,
  FormSubmissionResponse,
  FormTemplateResponse,
  InstallFormTemplateRequest,
  ListFormSubmissionsResponse,
  ListFormTemplatesResponse,
  PublishFormRequest,
  ReorderFormFieldsRequest,
  ReorderFormFieldsResponse,
  ReviewFormSubmissionRequest,
  UpdateFormFieldRequest,
  UpdateFormRequest,
} from '@core/types/form';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FormStore {
  // List state
  items: Form[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Form | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Form>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Form | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Form | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Archive form
   */
  async ArchiveForm(form_id: string, data: ArchiveFormRequest): Promise<FormResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ArchiveForm(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Close form
   */
  async CloseForm(form_id: string, data: CloseFormRequest): Promise<FormResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.CloseForm(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a new form
   */
  async CreateForm(data: CreateFormRequest): Promise<Form> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await formApi.CreateForm(data);
      const item = response.form;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create form field
   */
  async CreateFormField(form_id: string, data: CreateFormFieldRequest): Promise<FormFieldResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.CreateFormField(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Submit form response
   */
  async CreateFormSubmission(form_id: string, data: CreateFormSubmissionRequest): Promise<FormSubmissionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.CreateFormSubmission(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create form template
   */
  async CreateFormTemplate(data: CreateFormTemplateRequest): Promise<FormTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.CreateFormTemplate(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Delete form
   */
  async DeleteForm(form_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await formApi.DeleteForm(form_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== form_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === form_id) {
          this.currentItem = null;
        }

        this.removeFromCache(form_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete form field
   */
  async DeleteFormField(field_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await formApi.DeleteFormField(field_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== field_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === field_id) {
          this.currentItem = null;
        }

        this.removeFromCache(field_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete form submission
   */
  async DeleteFormSubmission(submission_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await formApi.DeleteFormSubmission(submission_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== submission_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === submission_id) {
          this.currentItem = null;
        }

        this.removeFromCache(submission_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Duplicate form
   */
  async DuplicateForm(form_id: string, data: DuplicateFormRequest): Promise<FormResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.DuplicateForm(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Export form submissions
   */
  async ExportFormSubmissions(form_id: string, params?: { format?: string | number | boolean; }): Promise<ExportFormSubmissionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ExportFormSubmissions(form_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get form by ID
   */
  async GetForm(form_id: string): Promise<Form> {
    // Check cache first
    const cached = this.cache.get(form_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await formApi.GetForm(form_id);
      const item = response.form;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get form analytics
   */
  async GetFormAnalytics(form_id: string): Promise<FormAnalyticsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.GetFormAnalytics(form_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get form submission
   */
  async GetFormSubmission(submission_id: string): Promise<FormSubmissionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.GetFormSubmission(submission_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Get form template
   */
  async GetFormTemplate(template_id: string): Promise<FormTemplateResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.GetFormTemplate(template_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Install form template
   */
  async InstallFormTemplate(template_id: string, data: InstallFormTemplateRequest): Promise<FormResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.InstallFormTemplate(template_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List form submissions
   */
  async ListFormSubmissions(form_id: string, params?: PaginatedRequest): Promise<ListFormSubmissionsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ListFormSubmissions(form_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List form templates
   */
  async ListFormTemplates(params?: PaginatedRequest): Promise<ListFormTemplatesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ListFormTemplates(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List forms
   */
  async ListForms(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await formApi.ListForms(params);

      runInAction(() => {
        this.items = response.items?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Publish form
   */
  async PublishForm(form_id: string, data: PublishFormRequest): Promise<FormResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.PublishForm(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Reorder form fields
   */
  async ReorderFormFields(form_id: string, data: ReorderFormFieldsRequest): Promise<ReorderFormFieldsResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ReorderFormFields(form_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Review form submission
   */
  async ReviewFormSubmission(submission_id: string, data: ReviewFormSubmissionRequest): Promise<FormSubmissionResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.ReviewFormSubmission(submission_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update form
   */
  async UpdateForm(form_id: string, data: UpdateFormRequest): Promise<Form> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await formApi.UpdateForm(form_id, data);
      const item = response.form;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Update form field
   */
  async UpdateFormField(field_id: string, data: UpdateFormFieldRequest): Promise<FormFieldResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await formApi.UpdateFormField(field_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const formStore = new FormStore();
